import { CustomerAddress } from '@/models/addressModels';
import { capitalize, nullOrEmpty } from '@/services/stringService';
import { FullOrder, OrderHeader } from '../cartModels';
import UserProfile from './userProfile';
import { CreditCard } from '../PaymentModels';
import { stringToBool } from '@/services/stringService';
import CommunicationPreferences from './communicationPreferences';
import { MONTHS } from '@/constants';

export class LoginReq {
    email: string;
    password: string;
    persistLogin: boolean;

    constructor() {
        this.email = '';
        this.password = '';
        this.persistLogin = false;
    }
}
export class Account extends UserProfile {
    address1: string;
    address2: string;
    addressType: string;
    businessName: string;
    cart: FullOrder;
    city: string;
    deliveryInstructions: string;
    emailList: any[];
    frontendTypeId: string;
    memberSince: string;
    nickname: string;
    state: string;
    zipCode: string;
    storeNumber: string;
    deliveryAddresses: CustomerAddress[];
    savedCards: CreditCard[];
    emailId: string;
    emailIsVerified: boolean;
    emailIsFavorite: boolean;
    extension: string;
    phoneType: string;
    phoneIsValidated: boolean;
    phoneIsFavorite: boolean;
    birthdate: string;
    customerZip?: string;
    pendingEmail: string | undefined;
    favoriteEmailId: number | null;
    favoritePhoneId: number | null;
    favoriteAddressId?: string;
    receiveDonatosNewsAndEventEmails: boolean;
    ltdCommunicationEmail: boolean;
    lrmEmails: boolean;
    receivePersonalizedOffers: boolean;
    receiveSmsOffers: boolean;
    receiveMobileAppOffers: boolean;
    isMarketingEmail: boolean;
    rewardssummary: RewardsAccountSummary | null;

    phoneId?: number;

    // Needed to create order header during checkout
    tempFirstName?: string;
    tempLastName?: string;
    tempEmail?: string;
    tempPhone?: string;

    constructor(data?: Partial<Account>) {
        super(data);
        //@ts-ignore
        if (data) data.isFavorite = !!nullOrEmpty(data?.address1); // derived for CustomerAddress below but not from api
        //@ts-ignore
        this.deliveryAddresses = (data?.addressList ?? data?.deliveryAddresses?.DeliveryAddress ?? []).map((a) => new CustomerAddress(a));

        this.customerId = data?.customerId ?? '';
        this.emailId = data?.emailId ?? '';
        this.emailList = data?.emailList ?? [];
        this.address1 = data?.address1 ?? '';
        this.address2 = data?.address2 ?? '';
        this.addressType = data?.addressType ?? '1';
        this.city = data?.city ?? '';
        this.state = data?.state ?? '';
        //@ts-ignore
        this.zipCode = data?.zipCode ?? data?.zip ?? data?.customerZip ?? '';
        this.storeNumber = data?.storeNumber ?? '';
        this.memberSince = data?.memberSince ?? '';
        this.businessName = data?.businessName ?? '';
        this.nickname = data?.nickname ?? '';
        this.cart = data?.cart ? new FullOrder(data?.cart) : ({} as FullOrder);
        this.deliveryInstructions = data?.deliveryInstructions ?? '';
        this.frontendTypeId = data?.frontendTypeId ?? '';
        this.phoneType = data?.phoneType ?? '';
        this.phoneId = data?.phoneId;
        this.cart = data?.cart ? new FullOrder(data?.cart) : ({} as FullOrder);
        this.deliveryInstructions = data?.deliveryInstructions ?? '';
        this.frontendTypeId = data?.frontendTypeId ?? '';
        this.savedCards = data?.savedCards ?? [];
        this.pendingEmail = data?.pendingEmail ?? undefined;
        this.emailId = data?.emailId ?? '';
        this.emailIsVerified = data?.emailIsVerified ?? false;
        this.emailIsFavorite = data?.emailIsFavorite ?? false;
        this.extension = data?.extension ?? '';
        this.phoneType = data?.phoneType ?? '';
        this.phoneIsValidated = data?.phoneIsValidated ?? false;
        this.phoneIsFavorite = data?.phoneIsFavorite ?? false;
        this.birthdate = data?.birthdate ?? '';
        this.favoriteEmailId = data?.favoriteEmailId ?? null;
        this.favoritePhoneId = data?.favoritePhoneId ?? null;
        this.favoriteAddressId = data?.favoriteAddressId;
        this.receiveDonatosNewsAndEventEmails = stringToBool(data?.receiveDonatosNewsAndEventEmails) ?? false;
        this.ltdCommunicationEmail = stringToBool(data?.ltdCommunicationEmail) ?? false;
        this.lrmEmails = stringToBool(data?.lrmEmails) ?? false;
        this.receivePersonalizedOffers = stringToBool(data?.receivePersonalizedOffers) ?? false;
        this.receiveSmsOffers = stringToBool(data?.receiveSmsOffers) ?? false;
        this.receiveMobileAppOffers = stringToBool(data?.receiveMobileAppOffers) ?? false;
        this.isMarketingEmail = stringToBool(data?.isMarketingEmail);
        this.savedCards = data?.savedCards ?? [];
        this.customerZip = data?.customerZip;
        this.rewardssummary = data?.rewardssummary ?? null;

        if (this.favoriteAddressId) {
            const findFavorite = this.deliveryAddresses.find((a) => a.addressId == this.favoriteAddressId);
            if (findFavorite) findFavorite.isFavorite = true;
        }
    }
    get name() {
        return `${this.firstName} ${this.lastName}`;
    }

    get asOrderHeader() {
        return new OrderHeader({
            customerId: this.customerId,
            firstName: nullOrEmpty(this.firstName) ?? this.tempFirstName,
            lastName: nullOrEmpty(this.lastName) ?? this.tempLastName,
            email: nullOrEmpty(this.tempEmail) ?? this.emailAddress,
            phoneNumber: nullOrEmpty(this.tempPhone) ?? this.phoneNumber ?? '',
            orderFor: nullOrEmpty(`${this.tempFirstName ?? ''} ${this.tempLastName ?? ''}`) ?? `${this.firstName} ${this.lastName}`,
            orderForName: nullOrEmpty(`${this.tempFirstName ?? ''} ${this.tempLastName ?? ''}`) ?? `${this.firstName} ${this.lastName}`,
            isMarketingEmail: +this.isMarketingEmail,
        });
    }

    get userProfile() {
        return new UserProfile(this);
    }
    get commPrefs() {
        return new CommunicationPreferences(this);
    }

    get hasValidBirthdate() {
        return !isNaN(new Date(this.birthdate.replace(/-/g, '/')).getDate());
    }
    get birthDay() {
        return this.hasValidBirthdate ? new Date(this.birthdate.replace(/-/g, '/')).getDate() : '';
    }
    get birthMonth() {
        const monthIndex = new Date(this.birthdate.replace(/-/g, '/')).getMonth();
        return this.hasValidBirthdate ? capitalize(MONTHS[monthIndex]) : '';
    }
}

export interface RewardsAccountSummary {
    pointsBalance: number;
    pointsLifetime: number;
    bankedCurrency: number;
    currentMembershipLevel: string;
    unredeemedCards: number;
    pendingPoints: number;
}

export interface GenerateTokenResponse {
    token: string;
}
